import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cytoscape, { type Core, type ElementDefinition } from 'cytoscape';
import fcose from 'cytoscape-fcose';
import { EDGE_COLOR, edgeTypesOf } from './infra-graph-elements.js';

// fcose レイアウト拡張を登録する。モジュール本体はインポート時に 1 度だけ評価されるため、
// cytoscape.use の多重登録は起こらない（モジュールシングルトンが保証）。
cytoscape.use(fcose);

/** InfraGraphView の props。 */
export interface InfraGraphViewProps {
  /** cytoscape 要素（俯瞰 or stack 詳細） */
  elements: ElementDefinition[];
  /** レイアウト調整用のモード（俯瞰は広く展開する） */
  layoutMode: 'overview' | 'detail';
  /** stack ノードクリック時のコールバック（俯瞰でのみ発火） */
  onSelectStack: (stackId: string) => void;
}

/** cytoscape スタイルシート（ノードはアイコン背景、エッジは data(color)）。 @graph-connects none */
const STYLESHEET: cytoscape.StylesheetStyle[] = [
  {
    selector: 'node',
    style: {
      'background-color': '#ffffff',
      'background-fit': 'contain',
      'background-clip': 'none',
      'background-image': 'data(icon)',
      shape: 'round-rectangle',
      label: 'data(label)',
      color: '#cbd5e1',
      'font-size': 11,
      'text-valign': 'bottom',
      'text-margin-y': 4,
      'text-wrap': 'wrap',
      'text-max-width': '110px',
      width: 34,
      height: 34,
      'text-outline-color': '#0b1220',
      'text-outline-width': 2,
    },
  },
  {
    selector: 'node[type="stack"]',
    style: { width: 'data(size)', height: 'data(size)', 'font-size': 12 },
  },
  { selector: 'node.dim', style: { opacity: 0.12 } },
  { selector: 'node.hl', style: { 'border-width': 4, 'border-color': '#7dd3fc' } },
  {
    selector: 'edge',
    style: {
      width: 'data(width)',
      'line-color': 'data(color)',
      'target-arrow-color': 'data(color)',
      'target-arrow-shape': 'triangle',
      'arrow-scale': 0.8,
      'curve-style': 'bezier',
      opacity: 0.6,
    },
  },
  { selector: 'edge.dim', style: { opacity: 0.04 } },
  { selector: '.hidden', style: { display: 'none' } },
];

/**
 * fcose レイアウトのオプションをモード別に返す。
 * @param mode レイアウトモード
 * @returns cytoscape layout options
 * @graph-connects none
 */
function layoutOptions(mode: 'overview' | 'detail'): cytoscape.LayoutOptions {
  const tuned =
    mode === 'overview'
      ? { nodeSeparation: 260, idealEdgeLength: 320, nodeRepulsion: 60000, gravity: 0.08 }
      : { nodeSeparation: 110, idealEdgeLength: 130, nodeRepulsion: 14000, gravity: 0.25 };
  return {
    name: 'fcose',
    quality: 'proof',
    animate: false,
    packComponents: true,
    randomize: true,
    ...tuned,
  } as unknown as cytoscape.LayoutOptions;
}

/**
 * infra-graph の cytoscape ビュー。俯瞰 / stack 詳細の要素を描画し、エッジ種別トグル・
 * 名前検索ハイライト・ズーム/パンを提供する。stack ノードのクリックで詳細へドリルダウンする。
 *
 * @graph-node {Page}
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business infra-graph 構成図の描画コンポーネント。公式 GCP アイコン付きで stack 俯瞰と stack 内部を可視化し、ドリルダウン・エッジトグル・検索を提供する。
 * @graph-connects infra-graph-elements [calls] エッジ種別抽出・色定数
 */
export function InfraGraphView({ elements, layoutMode, onSelectStack }: InfraGraphViewProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cyRef = useRef<Core | null>(null);
  const onSelectStackRef = useRef(onSelectStack);
  onSelectStackRef.current = onSelectStack;

  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');

  const edgeTypes = useMemo(() => edgeTypesOf(elements), [elements]);

  // cytoscape インスタンスはマウント時に 1 度だけ生成する。
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const cy = cytoscape({
      container,
      style: STYLESHEET,
      wheelSensitivity: 0.25,
      minZoom: 0.05,
      maxZoom: 4,
      elements: [],
    });
    cy.on('tap', 'node[type="stack"]', (event) => {
      const node = event.target as cytoscape.NodeSingular;
      const stackId: unknown = node.data('stackId');
      if (typeof stackId === 'string') {
        onSelectStackRef.current(stackId);
      }
    });
    // flex レイアウトでコンテナ高さが確定するのは初回ペイント後。0px で初期化された
    // canvas を、サイズ確定時に resize + fit して描画する。
    const resizeObserver = new ResizeObserver(() => {
      cy.resize();
      if (cy.elements().length > 0) {
        cy.fit(undefined, 30);
      }
    });
    resizeObserver.observe(container);
    cyRef.current = cy;
    return () => {
      resizeObserver.disconnect();
      cy.destroy();
      cyRef.current = null;
    };
  }, []);

  // 要素が変わったら差し替えてレイアウトし直す。
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.batch(() => {
      cy.elements().remove();
      cy.add(elements);
    });
    cy.resize();
    cy.layout(layoutOptions(layoutMode)).run();
    if (elements.length > 0) {
      cy.fit(undefined, 30);
    }
    setHiddenTypes(new Set());
    setSearch('');
  }, [elements, layoutMode]);

  // エッジ種別トグルを反映する。
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.edges().forEach((edge) => {
      const etype: unknown = edge.data('etype');
      edge.toggleClass('hidden', typeof etype === 'string' && hiddenTypes.has(etype));
    });
  }, [hiddenTypes, elements]);

  // 名前検索ハイライトを反映する。
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;
    const query = search.trim().toLowerCase();
    if (!query) {
      cy.elements().removeClass('dim hl');
      return;
    }
    cy.nodes().forEach((node) => {
      const haystack = `${String(node.data('label') ?? '')} ${String(node.data('full') ?? '')}`;
      const hit = haystack.toLowerCase().includes(query);
      node.toggleClass('hl', hit);
      node.toggleClass('dim', !hit);
    });
    cy.edges().addClass('dim');
  }, [search]);

  const toggleType = (type: string) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  return (
    <div className="relative flex-1 min-h-0">
      <div ref={containerRef} className="h-full w-full rounded-lg bg-[#0b1220]" />

      <div className="absolute top-3 left-3 z-10">
        <input
          type="text"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
          }}
          placeholder={t('infraGraph.searchPlaceholder')}
          aria-label={t('infraGraph.searchLabel')}
          className="px-3 py-1.5 text-sm rounded-md bg-[var(--color-bg-surface)] border border-[var(--color-border)] text-[var(--color-text-primary)] w-56"
        />
      </div>

      {edgeTypes.length > 0 && (
        <div className="absolute bottom-3 right-3 z-10 glass-card p-3 text-xs max-w-[240px]">
          <p className="mb-2 text-[var(--color-text-muted)] uppercase tracking-wide">
            {t('infraGraph.edgeTypes')}
          </p>
          <div className="space-y-1">
            {edgeTypes.map((type) => (
              <label key={type} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!hiddenTypes.has(type)}
                  onChange={() => {
                    toggleType(type);
                  }}
                />
                <span
                  className="inline-block h-[3px] w-5 rounded"
                  style={{ background: EDGE_COLOR[type] ?? '#64748b' }}
                />
                <span className="text-[var(--color-text-secondary)]">{type}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
